import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider, focusManager } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosError } from 'axios';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigation } from 'react-router';

import NotificationComponent from '../components/molecules/notification';
import PageSpinner from '../components/molecules/pageSpinner';
import AlertDialog from '../components/organisms/alertDialog';
import SideMenu from '../components/organisms/sideMenu';
import { customGoTheme } from '../material-ui-theme/tsGoTheme';
import { userKeys } from '../queries/query-keys';
import { sessionKeys } from '../queries/sessionQuery';
import { setTime } from '../utils/setTime';
import usePortal from '../utils/usePortal';
import { useZustand } from '../zustandStore';

import { excludedPaths } from './GoApp';
import { savePreLoginRouterState } from './routerUtils';

const ReactQueryDevtoolsProduction = lazy(() =>
	import('@tanstack/react-query-devtools/build/modern/production.js').then(d => ({
		default: d.ReactQueryDevtools,
	})),
);

// disable focus manager for tanstack-query
// so when switching tabs it does not refetch data
focusManager.setFocused(false);
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			enabled: false, // disbaled so we do not make unathorized use requests
			refetchOnWindowFocus: false,
			staleTime: setTime(1, 'minutes'),
			throwOnError: (error: unknown, query) => {
				if (
					error instanceof AxiosError &&
					(error.response?.status === 401 || error.response?.status === 403) &&
					(query.queryKey[0] === userKeys.getUser || query.queryKey[0] === sessionKeys.getStatus()) &&
					globalThis.location.pathname !== '/' &&
					!excludedPaths.includes(globalThis.location.pathname)
				) {
					savePreLoginRouterState({
						redirectTo: globalThis.location.pathname,
						redirectParams: globalThis.location.search,
					});

					// Navigate to login page
					Navigate({ to: '/signin/login' });
					return false;
				}

				if (error instanceof AxiosError) {
					return error.response?.status ? error.response.status >= 500 : false;
				}
				return false;
			},
		},
	},
});

const Root: FC<PropsWithChildren<{ children: ReactNode }>> = ({ children }) => {
	const navigation = useNavigation();
	const location = useLocation();
	const [showDevtools, setShowDevtools] = useState(false);
	const isInExcludedList = excludedPaths.some(path => location.pathname.includes(path));
	const shouldRenderSideMenu = location.pathname !== '/' && !isInExcludedList;
	queryClient.setDefaultOptions({
		queries: {
			enabled: shouldRenderSideMenu,
		},
	});

	const { pageSpinner } = useZustand(state => ({
		...state,
	}));
	const { isLoading: isPageLoading } = pageSpinner;
	const NotificationPortal = usePortal('notificationWrapper', <NotificationComponent />);
	const DialogPortal = usePortal('dialogWrapper', <AlertDialog />);

	useEffect(() => {
		globalThis.toggleDevtools = () => setShowDevtools(old => !old);
	}, []);

	useEffect(() => {
		if (!shouldRenderSideMenu) {
			return;
		}
		const menuWrapper = document.querySelector('#main-sideMenu');
		const hasOpenChild = menuWrapper?.querySelector('.side-menu-open');
		const collapsedChildren = menuWrapper?.querySelectorAll('.side-menu-collapsed');
		const collapsedAndOpenChild = menuWrapper?.querySelector('.side-menu-collapsed .open');

		menuWrapper?.classList.toggle('hasSidemenuOpen', Boolean(hasOpenChild));

		menuWrapper?.classList.toggle(
			'hasDoubleSidemenuCollapsed',
			Boolean(collapsedChildren && collapsedChildren.length >= 2),
		);

		menuWrapper?.classList.toggle(
			'hasCollapsedAndOpen',
			Boolean(collapsedChildren && collapsedChildren.length >= 2 && collapsedAndOpenChild),
		);
	}, [shouldRenderSideMenu]);

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider theme={customGoTheme}>
				<CssBaseline />
				{shouldRenderSideMenu ? <SideMenu /> : null}
				<>{NotificationPortal}</>
				<>{DialogPortal}</>
				<PageSpinner isLoading={isPageLoading || navigation.state === 'loading'} />
				{children}
			</ThemeProvider>
			<ReactQueryDevtools
				initialIsOpen={false}
				buttonPosition={globalThis.IS_PLAYWRIGHT_RUNNING ? 'top-left' : 'bottom-right'}
			/>
			{/*  calling window.toggleDevtools() will download the devtools bundle and show them. */}
			{showDevtools && (
				<Suspense fallback={null}>
					<ReactQueryDevtoolsProduction />
				</Suspense>
			)}
		</QueryClientProvider>
	);
};

export default Root;
